import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import './RelatedProducts.css';

const API_BASE_URL = 'https://api.autocasses.com/api';

const RelatedProducts = ({ currentProductId, category, brand }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const productsPerPage = 8; // Augmenté pour afficher 2 lignes de 4 produits
  const carouselRef = useRef(null);
  const navigate = useNavigate();

  // Fonction pour récupérer les produits de la catégorie
  const fetchRelatedProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      // Si pas de catégorie ID, ne pas faire la requête
      if (!category) {
        setRelatedProducts([]);
        setLoading(false);
        return;
      }

      // Récupérer les produits par catégorie
      const response = await fetch(`${API_BASE_URL}/produit/categories/${category}`);
      
      if (response.ok) {
        const data = await response.json();
        
        if (data && data.produits) {
          // Filtrer le produit actuel
          const filteredProducts = data.produits.filter(
            product => product.id.toString() !== currentProductId?.toString()
          );
          
          // Transformer les données pour correspondre à la structure attendue
          const formattedProducts = filteredProducts.map(product => ({
            id: product.id.toString(),
            titre: product.titre,
            prix: product.prix,
            etat: product.etat,
            photos: product.photos || [],
            city: product.city,
            region: product.region,
            pays: product.pays,
            inStock: product.inActive !== false,
            user: {
              name: product.user?.nom || 'Vendeur',
              isVerified: product.user?.isVerified || false,
              rating: 4.5
            }
          }));
          
          setRelatedProducts(formattedProducts.slice(0, 12)); // Augmenté à 12 produits
        } else {
          setRelatedProducts([]);
        }
      } else {
        throw new Error('Erreur lors du chargement des produits');
      }
    } catch (error) {
      console.error('Erreur lors du chargement des produits similaires:', error);
      setError('Impossible de charger les produits similaires');
      setRelatedProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (category) {
      fetchRelatedProducts();
    }
  }, [category, currentProductId]);

  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return new Intl.NumberFormat('fr-CM', {
      style: 'currency',
      currency: 'XAF',
      minimumFractionDigits: 0
    }).format(price);
  };

  const getConditionColor = (condition) => {
    const conditionLower = condition?.toLowerCase();
    switch (conditionLower) {
      case 'new': case 'neuf': case 'neuve':
        return {
          text: '#10b981',
          bg: 'rgba(16, 185, 129, 0.1)',
        };
      case 'excellent':
        return {
          text: '#3b82f6',
          bg: 'rgba(59, 130, 246, 0.1)',
        };
      case 'good': case 'bon':
        return {
          text: '#8b5cf6',
          bg: 'rgba(139, 92, 246, 0.1)',
        };
      case 'moyen':
        return {
          text: '#f59e0b',
          bg: 'rgba(245, 158, 11, 0.1)',
        };
      default:
        return {
          text: '#9ca3af',
          bg: 'rgba(156, 163, 175, 0.1)',
        };
    }
  };

  const handleProductClick = (productId) => {
    navigate('/product', { 
      state: { productId: productId }
    });
  };

  const handleViewAll = () => {
    navigate('/rechercher', { 
      state: { 
        categoryId: category,
        categoryName: 'Produits similaires'
      }
    });
  };

  const nextPage = () => {
    if (currentPage < Math.ceil(relatedProducts.length / productsPerPage) - 1) {
      setCurrentPage(currentPage + 1);
      if (carouselRef.current) {
        carouselRef.current.scrollLeft += carouselRef.current.offsetWidth;
      }
    }
  };

  const prevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
      if (carouselRef.current) {
        carouselRef.current.scrollLeft -= carouselRef.current.offsetWidth;
      }
    }
  };

  const renderProductItem = (item, index) => {
    const conditionColors = getConditionColor(item.etat);
    
    // Obtenir la première photo ou une image par défaut
    const productImage = item.photos?.[0]?.photo_url || 
      'https://images.unsplash.com/photo-1549399542-7e3f8b79c341?w=800&auto=format&fit=crop';
    
    return (
      <div
        key={item.id}
        className="product-card"
        onClick={() => handleProductClick(item.id)}
      >
        {/* Product Image */}
        <div className="product-image-container">
          <img
            src={productImage}
            alt={item.titre}
            className="product-image"
            loading="lazy"
          />
          
          {/* Stock Status */}
          <div className={`stock-badge ${item.inStock ? 'in-stock' : 'out-of-stock'}`}>
            <span className="stock-text">
               {item.etat?.charAt(0).toUpperCase() + item.etat?.slice(1) || 'État inconnu'}
            </span>
          </div>

      
        </div>

        {/* Product Info */}
        <div className="product-info">
          <h3 className="product-title" title={item.titre}>
            {item.titre.length > 50 ? item.titre.substring(0, 50) + '...' : item.titre}
          </h3>
          
          {/* Price */}
          <div className="price-container">
            <span className="price">
              {formatPrice(item.prix)}
            </span>
          </div>
          
          {/* Location */}
          <div className="location-container">
            <Icon name="MapPin" size={14} color="#6b7280" />
            <span className="location-text" title={`${item.city || ''} ${item.region || ''} ${item.pays || ''}`.trim()}>
              {item.city || item.region || item.pays || 'Lieu inconnu'}
            </span>
          </div>
          
          {/* Seller Info */}
          <div className="seller-info">
            <div className="seller-container">
              <Icon name="User" size={12} color="#6b7280" />
              <span className="seller-name" title={item.user?.name || 'Vendeur'}>
                {item.user?.name || 'Vendeur'}
              </span>
              {item.user?.isVerified && (
                <Icon name="ShieldCheck" size={12} color="#10b981" />
              )}
            </div>
            
            {item.user?.rating && (
              <div className="rating-container">
                <Icon name="Star" size={12} color="#fbbf24" />
                <span className="rating-text">{item.user.rating}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Si pas de catégorie
  if (!category) {
    return null;
  }

  if (loading) {
    return (
      <div className="related-products-container">
        <div className="section-header">
          <div className="header-text">
            <h2 className="section-title">Produits similaires</h2>
            <p className="section-subtitle">Chargement...</p>
          </div>
        </div>
        <div className="loading-container">
          <div className="loader"></div>
        </div>
      </div>
    );
  }

  if (error || relatedProducts.length === 0) {
    return (
      <div className="related-products-container">
        <div className="section-header">
          <div className="header-text">
            <h2 className="section-title">Produits similaires</h2>
            <p className="section-subtitle">Aucun produit disponible</p>
          </div>
        </div>
        {error && (
          <div className="error-container">
            <Icon name="AlertCircle" size={32} color="#6b7280" />
            <p className="error-text">{error}</p>
          </div>
        )}
      </div>
    );
  }

  const totalPages = Math.ceil(relatedProducts.length / productsPerPage);
  const visibleProducts = relatedProducts.slice(
    currentPage * productsPerPage,
    (currentPage + 1) * productsPerPage
  );

  return (
    <div className="related-products-container">
      {/* Header */}
      <div className="section-header">
        <div className="header-text">
          <h2 className="section-title">Produits similaires</h2>
          <p className="section-subtitle">Autres produits de la même catégorie</p>
        </div>
        
        <div className="header-actions">
          {totalPages > 1 && (
            <div className="carousel-controls">
              <button 
                className={`carousel-button prev-button ${currentPage === 0 ? 'disabled' : ''}`}
                onClick={prevPage}
                disabled={currentPage === 0}
              >
                <Icon name="ChevronLeft" size={20} color="#3b82f6" />
              </button>
              
              <button 
                className={`carousel-button next-button ${currentPage === totalPages - 1 ? 'disabled' : ''}`}
                onClick={nextPage}
                disabled={currentPage === totalPages - 1}
              >
                <Icon name="ChevronRight" size={20} color="#3b82f6" />
              </button>
            </div>
          )}
          
          <button 
            className="view-all-button"
            onClick={handleViewAll}
          >
            <span className="view-all-text">Voir tout</span>
            <Icon name="ArrowRight" size={16} color="#3b82f6" />
          </button>
        </div>
      </div>

      {/* Products Grid - Modifié pour 4 cartes par ligne */}
      <div 
        ref={carouselRef}
        className="products-grid grid-4"
      >
        {visibleProducts.map((product, index) => renderProductItem(product, index))}
      </div>

      {/* Pagination Indicators */}
      {totalPages > 1 && (
        <div className="pagination-indicators">
          {Array.from({ length: totalPages }).map((_, index) => (
            <button
              key={index}
              className={`indicator ${index === currentPage ? 'active' : ''}`}
              onClick={() => setCurrentPage(index)}
            />
          ))}
        </div>
      )}

      {/* View All Button (Mobile) */}
      <div className="view-all-mobile">
        <Button 
          onClick={handleViewAll}
          variant="outline"
          fullWidth
        >
          Voir tous les produits similaires
          <Icon name="ArrowRight" size={16} color="#3b82f6" />
        </Button>
      </div>
    </div>
  );
};

export default RelatedProducts;